/**
 * Clean Chart: what the Clean button does, and whether it is on.
 *
 * SEMANTICS (the contract the chart and the tests hold to)
 *
 *  - `active` is the button. ON hides every group whose `hides[group]` is true;
 *    OFF shows the chart exactly as the user had it.
 *  - Clean never writes to any other store. Hiding is derived at render time:
 *    level toggles are masked (ChartView's levelVisibility), primitives are
 *    suppressed in the series' primitive registry, series markers / S/R lines
 *    are gated on it. Because nothing is snapshotted, there is no "previous
 *    visibility" to lose: OFF restores whatever the user's own settings say
 *    now, which is exactly what they had before (plus anything they changed
 *    meanwhile).
 *  - Pressing the button is a pure toggle of `active`. Pressing it any number
 *    of times, at any speed, only ever leaves the chart in one of two states,
 *    and the OFF state is identical to the state before the first press.
 *  - Toggling a level in the Levels panel while Clean is ON changes the saved
 *    level setting. If Clean hides that level's group it stays hidden while
 *    Clean is on and appears when Clean is turned off; if Clean does not hide
 *    that group it takes effect immediately. Clean wins while it is on.
 *  - Ticking/unticking a group in the Clean menu while Clean is ON applies
 *    immediately.
 *  - `active` and `hides` are persisted, so a reload, symbol switch or
 *    timeframe switch keeps Clean in the same state with the same result.
 *
 * `hides` records what Clean HIDES, not what it shows: "Clean hides these" is
 * the way the button is thought about, and it keeps the default (everything
 * true) meaning "Clean really does clean". A target added in a later version is
 * absent from an older persisted blob and is filled in from the defaults.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** A group of chart elements that Clean can hide. */
export type CleanTarget =
  | 'diamonds'
  | 'signals'
  | 'trades'
  | 'news'
  | 'levels'
  | 'movingAverages'
  | 'volumeStudies'
  | 'structure'
  | 'priceLabels';

export const CLEAN_TARGETS: CleanTarget[] = [
  'diamonds',
  'signals',
  'trades',
  'news',
  'levels',
  'movingAverages',
  'volumeStudies',
  'structure',
  'priceLabels',
];

/**
 * Everything hidden by default.
 *
 * The complaint that prompted this was Clean not hiding enough, so the default
 * has to be a complete clean. Anything you want kept is then an explicit opt-out
 * rather than something you have to discover is missing.
 */
const DEFAULT_HIDES: Record<CleanTarget, boolean> = {
  diamonds: true,
  signals: true,
  trades: true,
  news: true,
  levels: true,
  movingAverages: true,
  volumeStudies: true,
  structure: true,
  priceLabels: true,
};

export interface CleanChartState {
  /** Whether Clean is on (the button's pressed state). */
  active: boolean;
  /** True = Clean hides this group. */
  hides: Record<CleanTarget, boolean>;
  setActive: (active: boolean) => void;
  toggleActive: () => void;
  toggleTarget: (target: CleanTarget) => void;
  setTarget: (target: CleanTarget, hide: boolean) => void;
  setAll: (value: boolean) => void;
  resetToDefaults: () => void;
}

export const useCleanChartStore = create<CleanChartState>()(
  persist(
    (set) => ({
      active: false,
      hides: { ...DEFAULT_HIDES },

      setActive: (active) => set(() => ({ active })),
      toggleActive: () => set((state) => ({ active: !state.active })),

      setTarget: (target, hide) =>
        set((state) => ({ hides: { ...state.hides, [target]: hide } })),

      toggleTarget: (target) =>
        set((state) => ({
          hides: { ...state.hides, [target]: !state.hides[target] },
        })),

      setAll: (value) =>
        set(() => ({
          hides: CLEAN_TARGETS.reduce(
            (acc, t) => ({ ...acc, [t]: value }),
            {} as Record<CleanTarget, boolean>,
          ),
        })),

      // Restores what Clean hides; does not turn Clean on or off.
      resetToDefaults: () => set(() => ({ hides: { ...DEFAULT_HIDES } })),
    }),
    {
      name: 'clean-chart-config',
      // v0 persisted only `hides`; `active` lived in ChartView's useState and was
      // lost on reload. v1 persists both. merge() below does the actual
      // reconciliation, so migrate only has to pass the blob through.
      version: 1,
      partialize: (state) => ({ active: state.active, hides: state.hides }),
      migrate: (persisted) => persisted as CleanChartState,
      // A target added in a later version is absent from a persisted blob written
      // by an older one. Without this merge it would come back undefined and be
      // treated as "do not hide", so a new element would silently survive Clean —
      // exactly the bug this store exists to fix.
      merge: (persisted, current) => {
        const p = persisted as Partial<CleanChartState> | undefined;
        const saved = p?.hides;
        const hides = { ...DEFAULT_HIDES };
        // Only known targets, and only real booleans: a retired target name or a
        // corrupted value must not leak into the state.
        for (const t of CLEAN_TARGETS) {
          if (typeof saved?.[t] === 'boolean') hides[t] = saved[t];
        }
        return {
          ...current,
          active: typeof p?.active === 'boolean' ? p.active : false,
          hides,
        };
      },
    },
  ),
);

export const DEFAULT_CLEAN_HIDES: Readonly<Record<CleanTarget, boolean>> = DEFAULT_HIDES;

/** Is `group` hidden right now? (Clean on AND configured to hide it.) */
export function isCleanHidden(
  state: Pick<CleanChartState, 'active' | 'hides'>,
  group: CleanTarget,
): boolean {
  return state.active && state.hides[group] === true;
}

const MOVING_AVERAGE_LEVELS = ['ema9', 'ema21', 'ema200', 'vwap'];

/**
 * The Clean group that hides a levelsStore toggle: EMAs/VWAP are moving
 * averages, the Vest NQ volume pane is a volume study, the Vest volume-spike
 * whales and the order-flow markers are signal markers, the order-flow line
 * panes are volume studies, the rest are levels.
 */
export function cleanGroupForLevel(key: string): CleanTarget {
  if (key === 'vestVolume' || key === 'cmeVolSpikes' || key === 'vestVolSpikes') return 'volumeStudies';
  if (key === 'vestSpikes' || key === 'cmeSpikes' || key === 'bothSpikes' || key === 'vestCat' || key === 'wickRejection') return 'signals';
  if (key.startsWith('flowLine')) return 'volumeStudies'; // order-flow line panes (TradeFinder Lines)
  if (key.startsWith('flow')) return 'signals'; // order-flow (TradeFinder) markers
  return MOVING_AVERAGE_LEVELS.includes(key) ? 'movingAverages' : 'levels';
}

/** Labels for the toggle UI. Kept next to the type so the two cannot drift. */
export const CLEAN_TARGET_LABELS: Record<CleanTarget, { label: string; description: string }> = {
  diamonds: { label: 'Diamonds', description: 'Diamond markers, scores and confluence labels' },
  signals: { label: 'Signal markers', description: 'Signals, skipped, volume and Donchian markers, BOS labels, 🐋/🦈/🐋🦈 volume spikes, 🐱 Vest vs CME, 🪝 wick rejections, order-flow markers' },
  trades: { label: 'Trade markers', description: 'Your entries and exits on the chart' },
  news: { label: 'News & events', description: 'News markers, calendar lines and upcoming events' },
  levels: { label: 'Levels & price lines', description: 'HOD/LOD, PDH/PDL, IB, weekly/monthly and S/R lines' },
  movingAverages: { label: 'Moving averages', description: 'EMA 9 / 21 / 200 and VWAP' },
  volumeStudies: { label: 'Volume studies', description: 'Volume profile, cumulative delta, Vest NQ volume bars, volume-pane spike markers, order-flow line panes' },
  structure: { label: 'Zones & structure', description: 'FVG zones, displacement, magnet, trailing, fair price, round levels' },
  priceLabels: { label: 'Price labels', description: 'Price level labels on the right edge' },
};
